// Scrollender Ticker Tape am unteren Rand. Liest Live-Prices aus useMarketStore.
// Wenn keine Daten da sind (Cache leer), zeigen wir Platzhalter-Symbole.
//
// V3-Pattern (siehe CLAUDE.md feedback): NICHT selectMarketStats direkt
// abonnieren — derived selector liefert pro Call ein neues Object → infinite
// loop. Stattdessen fullData abonnieren + lokal mit useMemo derivieren.

import { useMemo } from 'react';
import { deriveMarketStats } from '@/lib/market-cache';
import { selectFullData, useMarketStore } from '@/stores/market-store';
import type { MarketStats } from '@/types/market';

interface TickerItem {
  symbol: string;
  price: string;
  change: number | null;
}

const FALLBACK_ITEMS: ReadonlyArray<TickerItem> = [
  { symbol: 'BTC', price: '—', change: null },
  { symbol: 'ETH', price: '—', change: null },
  { symbol: 'SOL', price: '—', change: null },
  { symbol: 'SPX', price: '—', change: null },
  { symbol: 'DXY', price: '—', change: null },
  { symbol: 'VIX', price: '—', change: null },
  { symbol: 'GOLD', price: '—', change: null },
];

export function TickerTape() {
  const fullData = useMarketStore(selectFullData);
  const stats = useMemo<MarketStats | null>(() => deriveMarketStats(fullData), [fullData]);

  const items = useMemo<ReadonlyArray<TickerItem>>(() => {
    if (!stats) return FALLBACK_ITEMS;
    return [
      toItem('BTC', stats.btc, { currency: true }),
      toItem('ETH', stats.eth, { currency: true }),
      toItem('SOL', stats.sol, { currency: true }),
      toItem('SPX', stats.spx),
      toItem('DXY', stats.dxy),
      toItem('VIX', stats.vix),
      toItem('GOLD', stats.gold, { currency: true }),
    ];
  }, [stats]);

  return (
    <div className="glass-bar relative h-8 shrink-0 overflow-hidden border-t border-white/5">
      <div className="ticker-tape absolute inset-0 flex items-center whitespace-nowrap">
        <Row items={items} />
        <Row items={items} aria-hidden />
      </div>
    </div>
  );
}

function Row({
  items,
  ...rest
}: { items: ReadonlyArray<TickerItem> } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className="flex items-center gap-5 px-6 text-[13px] tabular" {...rest}>
      {items.map((it, i) => (
        <span key={`${it.symbol}-${i}`}>
          <span className="text-neutral-500">{it.symbol}</span>{' '}
          <span className="text-neutral-200">{it.price}</span>{' '}
          {it.change != null ? (
            <span className={it.change >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
              {it.change >= 0 ? '+' : ''}
              {it.change.toFixed(1)}%
            </span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

interface QuoteLike {
  price?: number | null;
  change?: number | string | null;
}

function toItem(
  symbol: string,
  quote: QuoteLike | null | undefined,
  opts: { currency?: boolean } = {},
): TickerItem {
  if (!quote || quote.price == null) {
    return { symbol, price: '—', change: null };
  }
  const price = opts.currency
    ? `$${quote.price.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
    : quote.price.toLocaleString('en-US', { maximumFractionDigits: 2 });
  const changeNum = typeof quote.change === 'string' ? Number.parseFloat(quote.change) : (quote.change ?? null);
  return {
    symbol,
    price,
    change: typeof changeNum === 'number' && Number.isFinite(changeNum) ? changeNum : null,
  };
}
